import { Button, Grid, Menu, MenuItem } from "@mui/material";

import MapSection from "features/Dashboard/components/MapSection";
import ScriptSharesSection from "features/Dashboard/components/ScriptSharesSection";
import ShareholderType from "features/Dashboard/components/ShareholderType";

import { HorizontalNoGridBarChart, Title } from "components";
import {
  useExportShareholders,
  useGetShareholderList,
  useGetSharesDistributionBy,
  useGetTotalSharesScriptScriptless,
} from "features/Dashboard/service/DashboardService";
import "./DashboardPage.css";
import { useEffect, useMemo, useState } from "react";
import { DownloadOutlined } from "@mui/icons-material";
import InputDatePickerComponent from "components/input-date-picker/InputDatePickerComponent";
import { formatDate } from "utils/formatDate";
import { useLocation, useNavigate } from "react-router-dom";
import {
  generateUrlWithParams,
  paramParsify,
  paramStringify,
} from "utils/queryParams";
import ShareholderWithBlockedStatusSection from "features/Dashboard/components/ShareholderWithBlockedStatusSection";
import { getUserProfile } from "features/account/service/AccountProfileService";
import { formatEllipsis } from "utils/formatString";
import { formatNumber } from "utils/formatNumber";

import storage from "services/storage";
import file from "services/file";
import dayjs from "dayjs";

const DashboardPage = ({ setLayoutAction }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const today = dayjs();

  const companyIdLocalStorage = storage.getCompanyID();

  const [companyId, setCompanyId] = useState(companyIdLocalStorage);
  const [downloadAnchor, setDownloadAnchor] = useState(null);

  const { response: { data: sharesData } = {} } =
    useGetTotalSharesScriptScriptless({
      id: companyId,
    }) ?? {};

  const { response: { data: shareHoldersByTypeData } = {} } =
    useGetSharesDistributionBy({
      id: companyId,
      by: "type",
    }) ?? {};

  const { response: { data: shareHoldersByRegionData } = {} } =
    useGetSharesDistributionBy({
      id: companyId,
      by: "region",
    }) ?? {};

  const { response: { data: shareholderListOwnershipRaw } = {} } =
    useGetShareholderList({
      id: companyId,
      sortBy: "ownership",
      enableAutoFetchByParams: true,
    }) ?? {};

  const { response: { data: shareholderListTenureRaw } = {} } =
    useGetShareholderList({
      id: companyId,
      sortBy: "tenure",
      enableAutoFetchByParams: true,
    }) ?? {};

  const { response: { data: shareholderListBlockedStatusRaw } = {} } =
    useGetShareholderList({
      id: companyId,
      enableAutoFetchByParams: true,
      isBlockedStatus: true,
    }) ?? {};

  const { exportFn, loading: loadingExport } = useExportShareholders();

  const shareholderListOwnership = useMemo(
    () =>
      shareholderListOwnershipRaw?.shareholders?.map((el) => ({
        ...el,
        label: `${formatNumber.toMio(
          el?.total_shares,
        )} (${el?.total_shares_percentage?.toFixed(2)}%)`,
        value: el?.total_shares,
        leftLabel: formatEllipsis(el?.name),
        leftFullLabel: el?.name,
      })),
    [shareholderListOwnershipRaw],
  );
  const shareholderListTenure = useMemo(
    () =>
      shareholderListTenureRaw?.shareholders?.map((el) => ({
        ...el,
        label: formatDate.toYearMonthDuration(el?.tenure_duration_in_days),
        value: el?.tenure_duration_in_days,
        leftLabel: formatEllipsis(el?.name),
        leftFullLabel: el?.name,
      })),
    [shareholderListTenureRaw],
  );
  const shareholderListBlockedStatus = useMemo(
    () =>
      shareholderListBlockedStatusRaw?.shareholders?.map((el) => {
        let status;
        switch (el?.status) {
          case 2:
            status = (
              <div className='bg-[#FCEAED] rounded-full px-2 py-1 max-w-[120px]'>
                <span className='text-[#E52A49 text-xs'>Account blocked</span>
              </div>
            );
            break;
          case 3:
            status = (
              <div className='bg-[#FFEBC5] rounded-full px-2 py-1 max-w-[120px]'>
                <span className='text-[#FF5E03] text-xs'>Balance blocked</span>
              </div>
            );
            break;
          case 4:
            status = (
              <div className='bg-[#F39DAB] rounded-full px-2 py-1 max-w-[195px]'>
                <span className='text-[#60121F] text-xs'>
                  Account and Balance blocked
                </span>
              </div>
            );
            break;
          default:
            status = <></>;
            break;
        }

        return {
          ...el,
          sid: el?.sid || "-",
          status,
          total_shares: formatNumber.toAmount(el?.total_shares),
        };
      }),
    [shareholderListBlockedStatusRaw],
  );

  // render custom action in title layout
  useEffect(() => {
    const parsedQueryParams = paramParsify(location?.search);

    const handleOnChangeDatePicker = (payload) => {
      const date = formatDate.toAPIDate(payload);
      const stringifiedParams = paramStringify({ ...parsedQueryParams, date });
      navigate(generateUrlWithParams(location?.pathname, stringifiedParams), {
        replace: true,
      });
    };

    const closeDownloadMenu = () => {
      setDownloadAnchor(false);
    };

    setLayoutAction(
      <>
        <Button
          onClick={(e) => setDownloadAnchor(e.currentTarget)}
          className='h-[40px]'
          variant='outlined'
        >
          <DownloadOutlined />
        </Button>
        <Menu
          anchorEl={downloadAnchor}
          open={Boolean(downloadAnchor)}
          onClose={closeDownloadMenu}
        >
          <MenuItem
            key='download-pdf'
            disabled={loadingExport}
            onClick={() => {
              let elements = document.getElementsByClassName("dashboard-page");
              const companyEffectCode = storage.getItem("effectCode");
              file.saveAsPDF(
                elements[0],
                `${companyEffectCode} - ${dayjs(
                  parsedQueryParams?.date ||
                    sharesData?.effective_date ||
                    Date.now(),
                ).format("DDMMYYYY")}`,
              );
            }}
          >
            <span className='text-[14px] font-semibold'>.PDF</span>
          </MenuItem>
          <MenuItem
            key='download-csv'
            disabled={loadingExport}
            onClick={() =>
              exportFn({
                type: "csv",
                id: companyId,
                effectiveDate: sharesData?.effective_date,
              })
            }
          >
            <span className='text-[14px] font-semibold'>.CSV</span>
          </MenuItem>
          <MenuItem
            key='download-xlsx'
            disabled={loadingExport}
            onClick={() =>
              exportFn({
                type: "xlsx",
                id: companyId,
                effectiveDate: sharesData?.effective_date,
              })
            }
          >
            <span className='text-[14px] font-semibold'>.XLSX</span>
          </MenuItem>
          <MenuItem
            key='download-txt'
            disabled={loadingExport}
            onClick={() =>
              exportFn({
                type: "txt",
                id: companyId,
                effectiveDate: sharesData?.effective_date,
              })
            }
          >
            <span className='text-[14px] font-semibold'>.TXT</span>
          </MenuItem>
        </Menu>
        <InputDatePickerComponent
          value={formatDate.toObjectDayJs(
            parsedQueryParams?.date || sharesData?.effective_date,
          )}
          onChange={handleOnChangeDatePicker}
          maxDate={today}
          disableWeekends={true}
          limitToFiveYears={true}
        />
      </>,
    );

    return () => setLayoutAction();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [downloadAnchor, sharesData]);

  //  handle when company id local storage is still null at initial render inner page
  useEffect(() => {
    const getUserAPI = async () => {
      const response = await getUserProfile();
      setCompanyId(response?.data?.company_id);
    };

    if (!companyIdLocalStorage) {
      getUserAPI();
    }
  }, [companyIdLocalStorage]);

  return (
    <div className='dashboard-page grid gap-4'>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <div className='flex flex-col gap-4 h-full'>
            <ScriptSharesSection data={sharesData} />

            <ShareholderType data={shareHoldersByTypeData} />
          </div>
        </Grid>

        <Grid item xs={6}>
          <MapSection data={shareHoldersByRegionData} />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={6}>
          <div className='section map-section h-full'>
            <Title
              title='Top 10 Shareholders Ownership'
              tooltipText='All values represented in shares ownership.'
            />
            <HorizontalNoGridBarChart
              id='shareholder-ownership-chart'
              data={shareholderListOwnership}
            />
          </div>
        </Grid>

        <Grid item xs={6}>
          <div className='section map-section h-full'>
            <Title title='Top 10 Longest-Tenured Shareholders' />

            <HorizontalNoGridBarChart
              id='shareholder-tenure-chart'
              data={shareholderListTenure}
            />
          </div>
        </Grid>
      </Grid>

      <ShareholderWithBlockedStatusSection
        data={{
          ...shareholderListBlockedStatusRaw,
          shareholderListBlockedStatus,
        }}
      />
    </div>
  );
};

export default DashboardPage;
